// ListingSpecs.jsx
// Component for displaying detailed listing specifications and features

import React from 'react';
import { COLORS, LAYOUT } from '../../constants';

// Modern SVG Icons
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const PackageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27,6.96 12,12.01 20.73,6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const ListingSpecs = ({ listing }) => {
  if (!listing) return null;

  // Only show specs that actually exist in the listing
  const specs = [];
  
  // Only add condition if it exists and is not empty
  if (listing.condition && listing.condition.trim()) {
    specs.push({ label: 'Condition', value: listing.condition, icon: <InfoIcon /> });
  }

  // Add category as a spec since it always exists
  if (listing.category) {
    specs.push({ label: 'Category', value: listing.category, icon: <PackageIcon /> });
  }

  // Add quantity available if it exists
  if (listing.quantityAvailable !== undefined) {
    const qtyText = listing.quantityAvailable > 0 
      ? `${listing.quantityAvailable} available` 
      : 'Out of stock';
    specs.push({ label: 'Quantity', value: qtyText, icon: <PackageIcon /> });
  }

  // Add posting date
  if (listing.createdAt) {
    const formatDate = (timestamp) => {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    };
    specs.push({ label: 'Posted', value: formatDate(listing.createdAt), icon: <InfoIcon /> });
  }

  // Only show realistic features, not fake ones
  const features = [];
  
  // Only add features if they actually exist in the listing data
  if (listing.features && Array.isArray(listing.features) && listing.features.length > 0) {
    features.push(...listing.features);
  }

  // Only render if we have real specs (don't show fake data)
  if (specs.length === 0) {
    return null;
  }

  return (
    <div style={modernStyles.container}>
      <h3 style={modernStyles.sectionTitle}>Item Details</h3>
      
      <div style={modernStyles.content}>
        {/* Specifications */}
        {specs.length > 0 && (
          <div style={modernStyles.specsSection}>
            <h4 style={modernStyles.subsectionTitle}>Specifications</h4>
            <div style={modernStyles.specsGrid}>
              {specs.map((spec, index) => (
                <div key={index} style={modernStyles.specItem}>
                  <div style={modernStyles.specIcon}>
                    {spec.icon}
                  </div>
                  <div style={modernStyles.specContent}>
                    <span style={modernStyles.specLabel}>{spec.label}</span>
                    <span style={modernStyles.specValue}>{spec.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features - Only show if listing actually has features */}
        {features.length > 0 && (
          <div style={modernStyles.featuresSection}>
            <h4 style={modernStyles.subsectionTitle}>Features</h4>
            <div style={modernStyles.featuresList}>
              {features.map((feature, index) => (
                <div key={index} style={modernStyles.featureItem}>
                  <div style={modernStyles.checkIcon}>
                    <CheckIcon />
                  </div>
                  <span style={modernStyles.featureText}>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

// Modern styles
const modernStyles = {
  container: {
    paddingBottom: LAYOUT.spacing.lg,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.lg,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xl,
  },

  // Specifications
  specsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.md,
  },
  subsectionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.md,
  },
  specsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: LAYOUT.spacing.md,
  },
  specItem: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.md,
    padding: LAYOUT.spacing.md,
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.md,
    border: `1px solid ${COLORS.border}`,
  },
  specIcon: {
    width: '32px',
    height: '32px',
    backgroundColor: COLORS.white,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.primary,
    flexShrink: 0,
  },
  specContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xs,
  },
  specLabel: {
    fontSize: '0.75rem',
    color: COLORS.text.muted,
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  specValue: {
    fontSize: '0.875rem',
    color: COLORS.text.primary,
    fontWeight: '600',
    textTransform: 'capitalize',
  },

  // Features
  featuresSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.md,
  },
  featuresList: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.sm,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.md,
  },
  checkIcon: {
    width: '24px',
    height: '24px',
    backgroundColor: COLORS.success,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
    flexShrink: 0,
  },
  featureText: {
    fontSize: '0.875rem',
    color: COLORS.text.secondary,
    lineHeight: 1.5,
  },


};

export default ListingSpecs;